/**
 * Which appearance the app renders in.
 *
 * Three states, not two. "System" is not a third colour — it is the absence of
 * a recorded preference, and this codebase is careful elsewhere that an absent
 * value and a chosen value are different claims (the dictionaries test insists
 * a refusal to compute a trend must not read as a zero). A two-state toggle
 * would pin a preference the moment the operator touched either half, with no
 * way back to "whatever this device says".
 *
 * The provider deliberately writes only EXPLICIT choices onto <html>, and
 * removes the attribute for "system". The stylesheet resolves the system case
 * on its own through `prefers-color-scheme`, which means an operator on System
 * follows their OS live — including a change made while the page is open —
 * with no media-query listener here to keep in sync.
 *
 * Unlike the locale, there is no module-level twin of this state: nothing
 * outside React reads the theme, because CSS custom properties do all the work.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

export type ThemePreference = 'system' | 'light' | 'dark';

export const THEMES: readonly ThemePreference[] = ['system', 'light', 'dark'];

export const DEFAULT_THEME: ThemePreference = 'system';

/**
 * Duplicated in the pre-paint script in index.html. That script cannot import a
 * module — it has to run before the first paint — so the key and the rule for
 * reading it live in two places and must be changed together.
 */
const STORAGE_KEY = 'kso.theme';

function isTheme(value: unknown): value is ThemePreference {
  return value === 'system' || value === 'light' || value === 'dark';
}

/**
 * Reads the stored preference.
 *
 * Storage is unavailable in a Node test run and can throw in a browser with
 * site data blocked, so a failure here falls back to the default rather than
 * taking the app down over a preference.
 */
export function readStoredTheme(): ThemePreference {
  try {
    const stored = globalThis.localStorage?.getItem(STORAGE_KEY);
    return isTheme(stored) ? stored : DEFAULT_THEME;
  } catch {
    return DEFAULT_THEME;
  }
}

export function storeTheme(theme: ThemePreference): void {
  try {
    globalThis.localStorage?.setItem(STORAGE_KEY, theme);
  } catch {
    // A preference that cannot be persisted is still worth honouring for this
    // session; there is nothing useful to report to the operator here.
  }
}

/** Puts an explicit choice on <html>; "system" is the absence of the attribute. */
export function applyTheme(theme: ThemePreference): void {
  const root = globalThis.document?.documentElement;
  if (!root) return;

  if (theme === 'system') delete root.dataset.theme;
  else root.dataset.theme = theme;
}

type ThemeContextValue = {
  theme: ThemePreference;
  setTheme: (theme: ThemePreference) => void;
};

const ThemeContext = createContext<ThemeContextValue | null>(null);

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<ThemePreference>(readStoredTheme);

  // The pre-paint script already applied this value, so on first mount this is
  // a no-op. It exists for the paths that script cannot cover: a StrictMode
  // remount, and any environment where it did not run.
  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  const setTheme = useCallback((next: ThemePreference) => {
    setThemeState(next);
    storeTheme(next);
    applyTheme(next);
  }, []);

  const value = useMemo(() => ({ theme, setTheme }), [theme, setTheme]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const context = useContext(ThemeContext);
  if (!context) throw new Error('useTheme must be used inside a ThemeProvider.');
  return context;
}
